import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { requireTenantUser } from '@/integrations/supabase/session';
import { toast } from '@/hooks/use-toast';
import { addDays } from 'date-fns';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useVisibility } from '@/hooks/useVisibility';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useTenant } from '@/hooks/useTenant';
import { formatDocDate } from '@/utils/locale';
import { formatInTz } from '@/utils/timezone';
import { taxFromNet } from '@/utils/tax';
import { CANCELLABLE_ESTIMATE_STATUSES } from '@/types/estimate';
import type {
  Estimate,
  EstimateWithRelations,
  EstimateStatus,
  CreateEstimateInput,
  EstimateLineItem,
  calculateEstimateTotals
} from '@/types/estimate';

interface EstimateFilters {
  status?: EstimateStatus | 'all';
  search?: string;
  startDate?: Date;
  endDate?: Date;
}

// Fetch all estimates with optional filters
export function useEstimates(filters?: EstimateFilters) {
  return useQuery({
    queryKey: ['estimates', filters],
    queryFn: async () => {
      let query = supabase
        .from('estimates')
        .select(`
          *,
          lead:leads(lead_number, status, customer_name),
          customer:customers(customer_name, customer_number, company_name),
          creator:profiles(full_name)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.search) {
        query = query.or(`estimate_number.ilike.%${filters.search}%,customer_name.ilike.%${filters.search}%,customer_phone.ilike.%${filters.search}%`);
      }

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }

      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return (data || []).map(item => ({
        ...item,
        line_items: typeof item.line_items === 'string' 
          ? JSON.parse(item.line_items) 
          : item.line_items || [],
      })) as EstimateWithRelations[];
    },
  });
}

// Fetch single estimate by ID
export function useEstimate(id: string | null) {
  return useQuery({
    queryKey: ['estimate', id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from('estimates')
        .select(`
          *,
          lead:leads(lead_number, status, customer_name),
          customer:customers(customer_name, customer_number, company_name),
          creator:profiles(full_name)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      
      return {
        ...data,
        line_items: typeof data.line_items === 'string' 
          ? JSON.parse(data.line_items) 
          : data.line_items || [],
      } as EstimateWithRelations;
    },
    enabled: !!id,
  });
}

// Fetch estimates for a specific lead
export function useLeadEstimates(leadId: string | null) {
  return useQuery({
    queryKey: ['lead-estimates', leadId],
    queryFn: async () => {
      if (!leadId) return [];
      
      const { data, error } = await supabase
        .from('estimates')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(item => ({
        ...item,
        line_items: typeof item.line_items === 'string' 
          ? JSON.parse(item.line_items) 
          : item.line_items || [],
      })) as Estimate[];
    },
    enabled: !!leadId,
  });
}

// Fetch estimates for a specific customer
export function useCustomerEstimates(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-estimates', customerId],
    queryFn: async () => {
      if (!customerId) return [];

      // Older estimates only know their lead; newer ones also carry customer_id.
      const { data: leadRows, error: leadErr } = await supabase
        .from('leads')
        .select('id')
        .eq('customer_id', customerId);
      if (leadErr) throw leadErr;
      const leadIds = (leadRows ?? []).map((l) => l.id);
      const filter = leadIds.length > 0
        ? `customer_id.eq.${customerId},lead_id.in.(${leadIds.join(',')})`
        : `customer_id.eq.${customerId}`;

      const { data, error } = await supabase
        .from('estimates')
        .select(`
          *,
          lead:leads(lead_number, status)
        `)
        .or(filter)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(item => ({
        ...item,
        line_items: typeof item.line_items === 'string' 
          ? JSON.parse(item.line_items) 
          : item.line_items || [],
      })) as EstimateWithRelations[];
    },
    enabled: !!customerId,
  });
}

// Create a new estimate (as draft)
/** Days a new quote stays valid: companies.estimate_valid_days (default 14). */
export function useEstimateValidDays(): number {
  const { company } = useTenant();
  const days = Number(company?.estimate_valid_days);
  return Number.isFinite(days) && days >= 1 ? days : 14;
}

export function useCreateEstimate() {
  const queryClient = useQueryClient();
  const tax = useCompanyTax();
  const { timezone } = useCompanyLocale();
  const validDays = useEstimateValidDays();

  return useMutation({
    mutationFn: async (input: CreateEstimateInput) => {
      const { user, tenantId: companyId } = await requireTenantUser();

      // Calculate totals using current company tax config; snapshot the rate
      // onto the row so future edits and reports stay consistent even if the
      // company changes its tax settings later.
      const subtotal = input.line_items.reduce((sum, item) => sum + item.amount, 0);
      const gstAmount = taxFromNet(subtotal, tax);
      const estimateAmount = subtotal + gstAmount;

      // Default validity: the company setting, counted on the company's calendar
      const validUntil = input.valid_until || formatInTz(addDays(new Date(), validDays), timezone, 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('estimates')
        .insert({
          company_id: companyId,
          lead_id: input.lead_id,
          customer_id: input.customer_id || null,
          customer_name: input.customer_name,
          customer_phone: input.customer_phone || null,
          sent_to_email: input.sent_to_email?.trim() || null,
          customer_address: input.customer_address || null,
          service_description: input.service_description || null,
          line_items: [...input.line_items],
          subtotal,
          gst_amount: gstAmount,
          estimate_amount: estimateAmount,
          tax_rate_snapshot: tax.rate,
          tax_label_snapshot: tax.label,
          tax_inclusive_snapshot: tax.inclusive,
          valid_until: validUntil,
          notes: input.notes || null,
          terms: input.terms || null,
          status: 'draft',
          estimate_number: '', // Let DB trigger generate
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['lead-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['customer-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      toast({
        title: 'Estimate created',
        description: 'Your estimate has been saved as a draft.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create estimate',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Update an existing estimate
export function useUpdateEstimate() {
  const queryClient = useQueryClient();
  const liveTax = useCompanyTax();

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: Partial<CreateEstimateInput> & { id: string; bill_to_edited_at?: string | null; deposit_percent?: number | null }) => {
      const updates: Record<string, unknown> = {};

      if (input.customer_name !== undefined) updates.customer_name = input.customer_name;
      if (input.customer_phone !== undefined) updates.customer_phone = input.customer_phone;
      if (input.sent_to_email !== undefined) updates.sent_to_email = input.sent_to_email;
      if (input.customer_address !== undefined) updates.customer_address = input.customer_address;
      if (input.service_description !== undefined) updates.service_description = input.service_description;
      if (input.notes !== undefined) updates.notes = input.notes;
      if (input.terms !== undefined) updates.terms = input.terms;
      if (input.valid_until !== undefined) updates.valid_until = input.valid_until;
      if (input.customer_id !== undefined) updates.customer_id = input.customer_id;
      // Stamped by the details dialog; the DB guard then shields the customer block from contact sync.
      if (input.bill_to_edited_at !== undefined) updates.bill_to_edited_at = input.bill_to_edited_at;
      if (input.deposit_percent !== undefined) updates.deposit_percent = input.deposit_percent;

      if (input.line_items) {
        // Fetch the row's snapshot so editing an old estimate doesn't pick up
        // a newer tax rate (matches the snapshot/moment-of-supply model).
        const { data: existing } = await supabase
          .from('estimates')
          .select('tax_rate_snapshot, tax_label_snapshot, tax_inclusive_snapshot')
          .eq('id', id)
          .single();
        const taxCfg = {
          enabled: liveTax.enabled,
          rate: existing?.tax_rate_snapshot ?? liveTax.rate,
          label: existing?.tax_label_snapshot ?? liveTax.label,
          inclusive: existing?.tax_inclusive_snapshot ?? liveTax.inclusive,
        };

        const subtotal = input.line_items.reduce((sum, item) => sum + item.amount, 0);
        const gstAmount = taxFromNet(subtotal, taxCfg);
        const estimateAmount = subtotal + gstAmount;

        updates.line_items = [...input.line_items];
        updates.subtotal = subtotal;
        updates.gst_amount = gstAmount;
        updates.estimate_amount = estimateAmount;
      }

      const { data, error } = await supabase
        .from('estimates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['lead-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['customer-estimates'] });
      toast({
        title: 'Estimate updated',
        description: 'Your changes have been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update estimate',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Send estimate via edge function
export function useSendEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ estimateId, message }: { estimateId: string; message?: string }) => {
      const { data, error } = await supabase.functions.invoke('send-estimate-email', {
        body: { estimateId, message },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate', variables.estimateId] });
      queryClient.invalidateQueries({ queryKey: ['lead-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      toast({
        title: 'Estimate sent',
        description: 'The estimate has been emailed to the customer.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to send estimate',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Mark estimate as accepted
export function useMarkEstimateAccepted() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (estimateId: string) => {
      const { data, error } = await supabase
        .from('estimates')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
        })
        .eq('id', estimateId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, estimateId) => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['lead-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      toast({
        title: 'Estimate accepted',
        description: 'The estimate has been marked as accepted.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update estimate',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Mark estimate as declined
export function useMarkEstimateDeclined() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ estimateId, reason }: { estimateId: string; reason?: string }) => {
      const { data, error } = await supabase
        .from('estimates')
        .update({
          status: 'declined',
          declined_at: new Date().toISOString(),
          decline_reason: reason || null,
          declined_by: 'staff',
        })
        .eq('id', estimateId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate', variables.estimateId] });
      queryClient.invalidateQueries({ queryKey: ['lead-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      toast({
        title: 'Estimate declined',
        description: 'The estimate has been marked as declined.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update estimate',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Cancel an estimate from the office side. Terminal: the customer sign link
// stops working (get-estimate-by-token refuses cancelled estimates).
export function useCancelEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ estimateId }: { estimateId: string }) => {
      const { data, error } = await supabase
        .from('estimates')
        .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
        .eq('id', estimateId)
        .in('status', CANCELLABLE_ESTIMATE_STATUSES)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('This estimate can no longer be cancelled.');
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate', variables.estimateId] });
      queryClient.invalidateQueries({ queryKey: ['lead-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['customer-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      toast({
        title: 'Estimate cancelled',
        description: 'The estimate has been cancelled and its customer link no longer works.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to cancel estimate',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Delete an estimate outright (mistakes). Any status: the UI keeps deleting a
// signed, accepted estimate to users who can see company financials.
export function useDeleteEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ estimateId }: { estimateId: string }) => {
      const { data, error } = await supabase
        .from('estimates')
        .delete()
        .eq('id', estimateId)
        .select('id')
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('This estimate could not be deleted. It may already be gone, or you may not have permission.');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['lead-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['customer-estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      toast({
        title: 'Estimate deleted',
        description: 'The estimate has been permanently removed.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete estimate',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Get estimate statistics
export function useEstimateStats() {
  const { canSeeCompanyFinancials } = useVisibility();
  const { timezone } = useCompanyLocale();
  return useQuery({
    queryKey: ['estimate-stats', timezone],
    enabled: canSeeCompanyFinancials,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('estimates')
        .select('status, estimate_amount, valid_until');

      if (error) throw error;

      // Calendar-day keys in the company timezone; valid_until is a date-only column.
      const today = formatInTz(new Date(), timezone, 'yyyy-MM-dd');
      const threeDaysFromNow = formatInTz(addDays(new Date(), 3), timezone, 'yyyy-MM-dd');

      const stats = {
        draft: { count: 0, total: 0 },
        sent: { count: 0, total: 0 },
        viewed: { count: 0, total: 0 },
        accepted: { count: 0, total: 0 },
        declined: { count: 0, total: 0 },
        expired: { count: 0, total: 0 },
        cancelled: { count: 0, total: 0 },
        expiringSoon: 0,
        acceptanceRate: 0,
      };

      (data || []).forEach(estimate => {
        const status = estimate.status as EstimateStatus;
        if (stats[status]) {
          stats[status].count++;
          stats[status].total += estimate.estimate_amount || 0;
        }

        // Check if expiring within 3 days
        if (estimate.valid_until && ['sent', 'viewed'].includes(status)) {
          const validUntil = formatDocDate(estimate.valid_until, 'yyyy-MM-dd', timezone);
          if (validUntil <= threeDaysFromNow && validUntil >= today) {
            stats.expiringSoon++;
          }
        }
      });

      // Calculate acceptance rate
      const responded = stats.accepted.count + stats.declined.count;
      if (responded > 0) {
        stats.acceptanceRate = Math.round((stats.accepted.count / responded) * 100);
      }

      return stats;
    },
  });
}

// Get estimates expiring soon (within 3 days)
export function useExpiringSoonEstimates() {
  const { timezone } = useCompanyLocale();
  return useQuery({
    queryKey: ['estimates-expiring-soon', timezone],
    queryFn: async () => {
      const today = formatInTz(new Date(), timezone, 'yyyy-MM-dd');
      const threeDaysFromNow = formatInTz(addDays(new Date(), 3), timezone, 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('estimates')
        .select(`
          *,
          lead:leads(lead_number, customer_name)
        `)
        .in('status', ['sent', 'viewed'])
        .gte('valid_until', today)
        .lte('valid_until', threeDaysFromNow)
        .order('valid_until', { ascending: true });

      if (error) throw error;
      
      return (data || []).map(item => ({
        ...item,
        line_items: typeof item.line_items === 'string' 
          ? JSON.parse(item.line_items) 
          : item.line_items || [],
      })) as EstimateWithRelations[];
    },
  });
}


export interface EstimateDepositState {
  amount: number;
  paid: number;
  percent: number | null;
  payment_url: string | null;
  reason?: 'not_accepted' | 'no_deposit' | 'paid' | 'not_connected' | 'no_location' | 'currency_mismatch' | 'square_error';
  message?: string;
}

const DEPOSIT_REASON_TEXT: Record<string, string> = {
  not_connected: 'Connect Square in Settings → Integrations to take deposits online.',
  no_location: 'Your Square account has no active location.',
  currency_mismatch: 'Your Square account settles in a different currency.',
  no_deposit: 'This estimate does not ask for a deposit.',
  paid: 'The deposit is already paid.',
  not_accepted: 'The estimate has not been accepted yet.',
};

/** Square pay link for the deposit on an accepted estimate (estimate-deposit-link; created on first call). */
export function useEstimateDepositLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (estimateId: string): Promise<EstimateDepositState> => {
      const { data, error } = await supabase.functions.invoke('estimate-deposit-link', { body: { estimate_id: estimateId } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const deposit = data?.deposit as EstimateDepositState;
      if (!deposit?.payment_url && deposit?.reason) {
        throw new Error(deposit.message || DEPOSIT_REASON_TEXT[deposit.reason] || 'No payment link available.');
      }
      return deposit;
    },
    onSuccess: (_, estimateId) => {
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
    },
  });
}
